import { useCallback, useEffect, useState } from "react";
import ConsolidatedService from "../../../services/ConsolidatedService";
import { ConsolidatedDashboardResponse } from "../../../entities/consolidated/ConsolidatedDashboardResponse";
import Loading from "../../shared/components/Loading";
import { TranslationService } from "../../../services/TranslationService";
import {
  formatCurrencyPortal,
  formatInteger
} from "../../../utils/FormatUtils";
import CompanyService from "../../../services/CompanyService";
import ErrorCatcher from "../../shared/ErrorCatcher";
import ConsolidatedDistributionChart from "./ConsolidatedDistributionChart";
import ConsolidatedCompaniesList from "./ConsolidatedCompaniesList";

const ConsolidatedDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dashboard, setDashboard] =
    useState<ConsolidatedDashboardResponse | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    const result = await ConsolidatedService.getDashboard();
    setLoading(false);

    if (result instanceof Error) {
      setError(true);
      return;
    }

    setDashboard(result);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { translate } = TranslationService;
  const showCents = CompanyService.getCompanyAuth()?.ShowCents ?? false;

  if (loading) {
    return <Loading height={400} />;
  }
  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-danger">
            {translate.ErrorLoadingData || "Error al cargar los datos"}
          </p>
        </div>
      </div>
    );
  }

  const total = dashboard?.TotalCredit ?? 0;
  const due = dashboard?.TotalOverdueCredit ?? 0;
  const nodue = total - due;
  const duePerc = parseFloat(dashboard?.DuePercentage ?? "0");
  const noduePerc = parseFloat(dashboard?.NoDuePercentage ?? "0");
  const currencySymbol = dashboard?.CurrencySymbol ?? "";

  return (
    <div className="container-fluid pb-0">
      <ErrorCatcher onError={() => setError(true)}>
        {/* Cards de totales */}
        <div className="dashboard__sales row mb-4">
          <div className="col-4 pe-0">
            <div className="dashboard__total card p-3">
              <div className="card-body px-2">
                <div className="card-header mb-1">
                  <p>{translate.TotalReceivables || "Total ventas a cobrar"}</p>
                  <span></span>
                </div>
                <h2>
                  {formatCurrencyPortal(total, currencySymbol, showCents)}
                </h2>
              </div>
            </div>
          </div>
          <div className="col-4 pe-0">
            <div className="dashboard__expired card p-3">
              <div className="card-body px-2">
                <div className="card-header mb-1">
                  <p>{translate.Dues || "Vencido"}</p>
                  <span>{formatInteger(duePerc)}%</span>
                </div>
                <h2>{formatCurrencyPortal(due, currencySymbol, showCents)}</h2>
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="dashboard__notExpired card p-3">
              <div className="card-body px-2">
                <div className="card-header mb-1">
                  <p>{translate.NoDues || "No vencido"}</p>
                  <span>{formatInteger(noduePerc)}%</span>
                </div>
                <h2>
                  {formatCurrencyPortal(nodue, currencySymbol, showCents)}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de distribución */}
        {dashboard && (
          <div className="mb-4">
            <ConsolidatedDistributionChart
              chartData={dashboard.ChartData}
              currencySymbol={currencySymbol}
            />
          </div>
        )}

        {/* Listado de empresas */}
        {dashboard && (
          <div className="mb-4">
            <ConsolidatedCompaniesList
              companies={dashboard.Companies}
              currencySymbol={currencySymbol}
            />
          </div>
        )}
      </ErrorCatcher>
    </div>
  );
};

export default ConsolidatedDashboard;
